import {
  Apple,
  Bell,
  CheckCircle2,
  Download,
  Laptop,
  MonitorDown,
  Monitor,
  Smartphone,
  Sparkles,
  Terminal,
} from 'lucide-react';
import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { StudentSidebar } from './StudentSidebar';

const DOWNLOAD_GROUPS = [
  {
    id: 'desktop',
    eyebrow: 'Desktop',
    title: 'Learn from your computer',
    description: 'A focused learning experience for your desktop or laptop.',
    icon: MonitorDown,
    tone: 'violet',
    platforms: [
      { name: 'Windows', detail: 'Windows 10 and later', icon: Monitor },
      { name: 'macOS', detail: 'For Mac computers', icon: Apple },
      { name: 'Linux', detail: 'For Linux distributions', icon: Terminal },
    ],
  },
  {
    id: 'mobile',
    eyebrow: 'Mobile',
    title: 'Keep learning wherever you go',
    description: 'Take your courses, classes and learning progress with you.',
    icon: Smartphone,
    tone: 'red',
    platforms: [
      { name: 'Android', detail: 'Phones and tablets', icon: Smartphone },
      { name: 'iOS', detail: 'iPhone and iPad', icon: Apple },
    ],
  },
];

export function StudentDownloadsPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const firstName = String(user?.displayName || user?.email || 'Student').split(/[ @]/)[0];
  const currentDate = useMemo(() => new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric',
  }).format(new Date()), []);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  function requestDownload(platform) {
    notify(`${platform} download will be available soon.`);
  }

  if (authLoading) return <LoadingState label="Loading student portal..." />;
  if (!user) return <Navigate to="/student/login" replace />;

  return (
    <main className="student-portal student-downloads-page">
      <StudentSidebar onLogout={onLogout} />

      <div className="student-downloads-shell">
        <section className="student-downloads-workspace">
          <header className="student-downloads-header">
            <div>
              <span>Welcome back, {firstName}</span>
              <h1>Downloads</h1>
              <p>{currentDate}</p>
            </div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications">
              <Bell size={19} strokeWidth={1.7} />
              <i />
            </button>
          </header>

          <div className="student-downloads-intro">
            <div className="student-downloads-intro-icon"><Download size={24} /></div>
            <div>
              <span><Sparkles size={13} /> Learn on your preferred device</span>
              <h2>Keep your learning<br /><em>within reach.</em></h2>
              <p>Choose the version that works best for you. Your learning experience will stay connected across devices.</p>
            </div>
          </div>

          <div className="student-downloads-groups">
            {DOWNLOAD_GROUPS.map(({ id, eyebrow, title, description, icon: GroupIcon, tone, platforms }) => (
              <section className={`student-downloads-group student-downloads-group--${tone}`} key={id}>
                <div className="student-downloads-group-heading">
                  <div className="student-downloads-group-title">
                    <span><GroupIcon size={18} /></span>
                    <div><small>{eyebrow}</small><h2>{title}</h2></div>
                  </div>
                  <p>{description}</p>
                </div>
                <div className="student-downloads-platforms">
                  {platforms.map(({ name, detail, icon: PlatformIcon }) => (
                    <article className="student-download-card" key={name}>
                      <div className="student-download-card-icon"><PlatformIcon size={25} strokeWidth={1.7} /></div>
                      <div className="student-download-card-copy"><h3>{name}</h3><p>{detail}</p></div>
                      <button type="button" onClick={() => requestDownload(name)} aria-label={`Download for ${name}`}>
                        <Download size={15} />
                      </button>
                      <span className="student-download-coming-soon"><CheckCircle2 size={12} /> Coming soon</span>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>

        <aside className="student-downloads-aside">
          <div className="student-downloads-aside-top"><Laptop size={19} /><span>One account</span></div>
          <div className="student-downloads-aside-art"><Download size={38} /></div>
          <h2>Learn your way</h2>
          <p>Start on one device and continue wherever your day takes you.</p>
          <div className="student-downloads-aside-note"><CheckCircle2 size={15} /><span>Your progress stays in sync.</span></div>
        </aside>
      </div>
    </main>
  );
}
